/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import MediaBatchPreparation from "./views/MediaBatchPreparation";
import MediaBatchCreation from "./views/MediaBatchCreation";
import MediaBatchSterilization from "./views/MediaBatchSterilization";
import MediaDispenseEntry from "./views/MediaDispenseEntry";
import ProcessFineTuning from "./views/MediaFineTuning";
import { MediaBatchPreparationInitServices } from "../../../service/mediaBatchPrepInitServices";
import { MediaBatchViewPreparationDetails } from "./types/mediaBatchCommon.types";
import { useSearchParams } from "react-router-dom";
import MediaFinalQualityReview from "./views/MediaFinalQualityReview";

const MediaPreparationBatchInit = () => {
	const [searchParams] = useSearchParams();
	const id = searchParams.get("batchId");
	const [batchId, setBatchId] = useState<number | null>(
		id ? Number(id) : null,
	);

	useEffect(() => {
		setBatchId(id ? Number(id) : null);
	}, [id]);
	// Default to 'MediaBatchCreation' if no ID is present
	const [view, setView] = useState(id ? "Loading" : "MediaBatchCreation");
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!id) return;

		const fetchBatchStatus = async () => {
			setIsLoading(true);
			try {
				const response: MediaBatchViewPreparationDetails =
					await MediaBatchPreparationInitServices.getViewPreparationDetails(
						{ id: Number(id) },
					);

				let batchStatus = response.data.status;
				if (batchStatus === "Media Dispensed") {
					if (
						response.data.dispensing_summary.dispensing_status ===
						"Pending"
					) {
						batchStatus = "Sterilized";
					}
				}

				switch (batchStatus) {
					case "Draft":
						setView("MediaBatchPreparation");
						break;
					case "In Preparation":
						setView("MediaBatchSterilization");
						break;
					case "Sterilized":
						setView("MediaDispenseEntry");
						break;
					case "Media Dispensed":
						setView("ProcessFineTuning");
						break;
					case "Fine Tuned":
						setView("FinalQualityReview");
						break;
					case "Ready to Use":
					case "Rejected":
						setView("ReadOnly");
						break;	
					default:
						setView("MediaBatchCreation");
				}
			} catch (error) {
				console.error("Failed to fetch batch details:", error);
				setView("MediaBatchCreation");
			} finally {
				setIsLoading(false);
			}
		};

		fetchBatchStatus();
	}, [id]);

	if (isLoading) {
		return <div></div>;
	}

	// Render components based on view state
	switch (view) {
		case "MediaBatchPreparation":
			return (
				<MediaBatchPreparation
					onNext={() => setView("MediaBatchSterilization")}
					onBack={() => setView("MediaBatchCreation")}
					batchId={batchId}
				/>
			);
		case "MediaBatchSterilization":
			if (!batchId) return null;
			return (
				<MediaBatchSterilization
					onNext={() => setView("MediaDispenseEntry")}
					onBack={() => setView("MediaBatchPreparation")}
					batchId={batchId}
				/>
			);
		case "MediaDispenseEntry":
			if (!batchId) return null;
			return (
				<MediaDispenseEntry
					onNext={() => setView("ProcessFineTuning")}
					onBack={() => setView("MediaBatchSterilization")}
					batchId={batchId}
				/>
			);
		case "ProcessFineTuning":
			return (
				<ProcessFineTuning
					onNext={() => setView("FinalQualityReview")}
					onBack={() => setView("MediaDispenseEntry")}
					batchId={batchId}
				/>
			);
		case "FinalQualityReview":
			if (!batchId) return null;
			return (
				<MediaFinalQualityReview
					onBack={() => setView("ProcessFineTuning")}
					batchId={batchId}
				/>
			);
		default:
			return (
				<MediaBatchCreation
					onNext={() => setView("MediaBatchPreparation")}
					batchId={batchId}
					setBatchId={setBatchId}
				/>
			);
	}
};

export default MediaPreparationBatchInit;
